// frontend/src/components/common/CommentThread.js
// Spec: "Comments and discussion on each part". Shared by parts and tickets.
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, List, ListItem, ListItemAvatar, ListItemText,
    Avatar, Typography, CircularProgress, Alert, Chip,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'ckeditor5/ckeditor5-content.css';
import { ROLE_LABELS } from '../../constants/domain';
import RichCommentEditor from './RichCommentEditor';
import { isBlankHtml, sanitizeCommentHtml } from '../../utils/richText';

const CommentThread = ({ onLoad, onAdd }) => {
    const [comments, setComments] = useState([]);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            setComments(await onLoad());
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        if (isBlankHtml(text)) return;
        setPosting(true);
        try {
            await onAdd(text);
            setText('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setPosting(false);
        }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : comments.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                    No comments yet. Start the discussion below.
                </Typography>
            ) : (
                <List sx={{ maxHeight: 340, overflow: 'auto' }}>
                    {comments.map(c => (
                        <ListItem key={c.id} alignItems="flex-start" disableGutters>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}>
                                    {(c.user_name || '?').charAt(0).toUpperCase()}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {c.user_name || 'Unknown'}
                                        </Typography>
                                        {c.user_role && (
                                            <Chip label={ROLE_LABELS[c.user_role] || c.user_role}
                                                size="small" variant="outlined"
                                                sx={{ height: 18, fontSize: 10 }} />
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(c.created_at).toLocaleString('en-GB')}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Box
                                        className="ck-content"
                                        sx={{
                                            mt: 0.25,
                                            '& p:first-of-type': { mt: 0 },
                                            '& p:last-of-type': { mb: 0 },
                                            '& table': { maxWidth: '100%' },
                                            '& img': { maxWidth: '100%', height: 'auto' },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: sanitizeCommentHtml(c.comment) }}
                                    />
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <Box component="form" onSubmit={submit} sx={{ mt: 1 }}>
                <RichCommentEditor
                    value={text}
                    onChange={setText}
                    disabled={posting}
                    placeholder="Add a comment... paste a table or image and it keeps its formatting"
                />
                <Button type="submit" variant="contained" disabled={posting || isBlankHtml(text)}
                    startIcon={posting ? <CircularProgress size={14} /> : <Send />}
                    sx={{ mt: 1 }}>
                    Post
                </Button>
            </Box>
        </Box>
    );
};

export default CommentThread;
